import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight, Languages, Briefcase, Wrench, Hammer, HardHat, ClipboardList, Users } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { I18N } from '@/lib/i18n';
import { PUBLIC_FORMS } from '@/lib/adminApi';

const POSITIONS_META = [
  { name: 'Roofing Crew Member', desc: 'Entry-level OK. Help install, tear-off, and repair residential and commercial roofs. We’ll train you.', icon: Hammer },
  { name: 'Experienced Roofer', desc: '2+ years roofing experience. Lead small jobs and mentor newer crew.', icon: Wrench },
  { name: 'Roofing Foreman / Crew Lead', desc: 'Run job sites, manage 2–4 person crews, ensure quality and safety.', icon: HardHat },
  { name: 'Stucco Technician', desc: 'Repair and apply stucco on residential and commercial. 1+ year experience preferred.', icon: Wrench },
  { name: 'General Laborer / Helper', desc: 'Support crews on roofing, stucco, fence, and exterior repair. Great entry point.', icon: Users },
  { name: 'Estimator / Project Coordinator', desc: 'Office and field hybrid. Customer-facing role for someone organized and good with people.', icon: ClipboardList },
];

const LANG_OPTIONS = ['English', 'Spanish', 'Portuguese', 'Haitian Creole', 'Other'];
const SOURCE_OPTIONS = ['Google', 'Indeed', 'Facebook', 'Instagram', 'Word of Mouth', 'Truck or Sign', 'Other'];
const EXPERIENCE_OPTIONS = ['None', '<1 year', '1-2 years', '3-5 years', '5+ years'];

function setSEO(title, desc) {
  document.title = title;
  let m = document.head.querySelector('meta[name="description"]');
  if (!m) { m = document.createElement('meta'); m.name = 'description'; document.head.appendChild(m); }
  m.setAttribute('content', desc);
}

function setJobPostingJsonLd(positions) {
  const id = 'careers-jobposting-jsonld';
  let el = document.getElementById(id);
  if (!el) { el = document.createElement('script'); el.id = id; el.type = 'application/ld+json'; document.head.appendChild(el); }
  const orgs = positions.map((p) => ({
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: p.name,
    description: p.desc,
    hiringOrganization: {
      '@type': 'Organization',
      name: 'Elite Roofing & Construction LLC',
      sameAs: 'https://elitercfl.com',
    },
    employmentType: 'FULL_TIME',
    jobLocation: {
      '@type': 'Place',
      address: { '@type': 'PostalAddress', addressLocality: 'Kissimmee', addressRegion: 'FL', addressCountry: 'US' },
    },
    datePosted: new Date().toISOString().slice(0, 10),
    validThrough: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
  }));
  el.textContent = JSON.stringify(orgs);
}

export default function Careers() {
  const [lang, setLang] = useState('en');
  const t = I18N[lang];
  const formRef = useRef(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // form state
  const [f, setF] = useState({
    full_name: '', phone: '', email: '', address: '', city: '', state: 'FL', zip_code: '',
    dob: '', position: '', years_experience: '', currently_employed: '',
    start_date: '', has_drivers_license: '', has_transportation: '', comfortable_with_heights: '',
    languages: [], certifications: '', work_authorization: '', why_join: '', source: '', consent: false, resume: null,
  });

  useEffect(() => {
    setSEO('Careers — Elite Roofing & Construction', 'Join the Elite Roofing team. We’re hiring crew members, foremen, stucco technicians, helpers, and estimators across Central Florida.');
    setJobPostingJsonLd(POSITIONS_META);
  }, []);

  const update = (key, val) => setF((s) => ({ ...s, [key]: val }));
  const toggleLang = (lng) => setF((s) => ({ ...s, languages: s.languages.includes(lng) ? s.languages.filter((x) => x !== lng) : [...s.languages, lng] }));

  const scrollToForm = (positionName) => {
    if (positionName) update('position', positionName);
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const validate = () => {
    const e = {};
    const required = ['full_name', 'phone', 'email', 'address', 'city', 'state', 'zip_code', 'dob', 'position', 'years_experience', 'currently_employed', 'start_date', 'has_drivers_license', 'has_transportation', 'comfortable_with_heights', 'work_authorization'];
    required.forEach((k) => { if (f[k] === '' || f[k] === undefined || f[k] === null) e[k] = 'Required'; });
    if (f.languages.length === 0) e.languages = 'Select at least one';
    if (!f.consent) e.consent = 'Required';
    if (f.work_authorization === 'no') e.work_authorization = 'Must be authorized to work in U.S.';
    if (f.email && !/^\S+@\S+\.\S+$/.test(f.email)) e.email = 'Invalid email';
    return e;
  };

  const submit = async () => {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) {
      toast.error('Please fill in all required fields');
      const first = Object.keys(e)[0];
      const el = document.querySelector(`[data-field="${first}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries({
        full_name: f.full_name, phone: f.phone, email: f.email, address: f.address, city: f.city, state: f.state, zip_code: f.zip_code,
        dob: f.dob, position: f.position, years_experience: f.years_experience,
        currently_employed: String(f.currently_employed === 'yes'),
        start_date: f.start_date,
        has_drivers_license: String(f.has_drivers_license === 'yes'),
        has_transportation: String(f.has_transportation === 'yes'),
        comfortable_with_heights: String(f.comfortable_with_heights === 'yes'),
        languages: f.languages.join(','),
        certifications: f.certifications || '',
        work_authorization: String(f.work_authorization === 'yes'),
        why_join: f.why_join || '',
        source: f.source || '',
        consent: String(Boolean(f.consent)),
      }).forEach(([k, v]) => fd.append(k, v));
      if (f.resume) fd.append('resume', f.resume);
      const res = await PUBLIC_FORMS.submitApplication(fd);
      toast.success(res.message || 'Application submitted!');
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to submit. Please try again.';
      toast.error(typeof detail === 'string' ? detail : 'Validation error. Check the form.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="flex-1 mx-auto max-w-screen-md px-4 sm:px-6 py-20 text-center" data-testid="careers-success">
          <div className="mx-auto h-16 w-16 rounded-full bg-emerald-50 flex items-center justify-center mb-6">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">{t.success_title}</h1>
          <p className="mt-3 text-slate-600 text-lg">{t.success_body}</p>
          <div className="mt-8"><Link to="/"><Button className="bg-[hsl(var(--accent))] hover:bg-[#a01830] text-white">Back to Home</Button></Link></div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="careers-page">
      <Navbar />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[hsl(212_51%_25%)] via-[#13294a] to-[#0f172a] text-white">
          <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-xs font-semibold uppercase tracking-wide">
                <Briefcase className="h-3.5 w-3.5" /> Careers
              </span>
              <div className="inline-flex items-center gap-1 rounded-full bg-white/10 border border-white/15 p-1 text-sm" data-testid="lang-toggle">
                <Languages className="h-4 w-4 mx-1" />
                <button onClick={() => setLang('en')} className={`px-3 py-1 rounded-full transition-colors ${lang === 'en' ? 'bg-white text-slate-900 font-semibold' : 'text-white/80 hover:text-white'}`} data-testid="lang-en">EN</button>
                <button onClick={() => setLang('es')} className={`px-3 py-1 rounded-full transition-colors ${lang === 'es' ? 'bg-white text-slate-900 font-semibold' : 'text-white/80 hover:text-white'}`} data-testid="lang-es">ES</button>
              </div>
            </div>
            <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight max-w-3xl" data-testid="careers-hero-title">{t.careers_title}</motion.h1>
            <p className="mt-5 text-lg text-white/85 max-w-2xl leading-relaxed">{t.careers_subhead}</p>
            <div className="mt-7">
              <Button onClick={() => scrollToForm()} className="h-12 px-7 bg-[hsl(var(--accent))] hover:bg-[#a01830] text-white shadow-lg font-semibold" data-testid="careers-apply-cta">
                {t.apply_now} <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        </section>

        {/* Why work here */}
        <section className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8 py-14">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6">{t.why_work_here}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="rounded-xl border border-border bg-card p-5 shadow-sm">
                <CheckCircle2 className="h-6 w-6 text-emerald-600 mb-3" />
                <h3 className="font-bold text-slate-900">{t[`why_${i}_title`]}</h3>
                <p className="mt-1 text-sm text-slate-600 leading-relaxed">{t[`why_${i}_body`]}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Open positions */}
        <section className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8 pb-14">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6">{t.open_positions}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {POSITIONS_META.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.name} className="rounded-xl border border-border bg-card p-5 shadow-sm hover:shadow-md transition-shadow" data-testid={`position-${p.name.replace(/\s+/g, '-').toLowerCase()}`}>
                  <div className="flex items-start gap-3 mb-3">
                    <div className="h-10 w-10 rounded-lg bg-[hsl(212_51%_25%_/_0.08)] flex items-center justify-center">
                      <Icon className="h-5 w-5 text-[hsl(var(--primary))]" />
                    </div>
                    <h3 className="font-bold text-slate-900 leading-snug pt-1">{p.name}</h3>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed mb-4">{p.desc}</p>
                  <Button variant="outline" size="sm" onClick={() => scrollToForm(p.name)} className="w-full hover:bg-[hsl(var(--primary))] hover:text-white hover:border-[hsl(var(--primary))]" data-testid={`apply-${p.name.replace(/\s+/g, '-').toLowerCase()}`}>
                    {t.apply_for_role}
                  </Button>
                </div>
              );
            })}
          </div>
        </section>

        {/* Requirements */}
        <section className="bg-slate-50 border-y border-border">
          <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8 py-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-5">{t.requirements_title}</h2>
            <ul className="grid sm:grid-cols-2 gap-3">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <li key={i} className="flex items-start gap-2 text-slate-700">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
                  <span className="text-sm sm:text-base">{t[`req_${i}`]}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Application Form */}
        <section ref={formRef} className="mx-auto max-w-3xl px-4 sm:px-6 py-14" id="apply">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">{t.apply_now}</h2>
          <p className="text-slate-600 mb-6">Fields marked with <span className="text-red-600">*</span> are required.</p>

          <FormSection title={t.section_personal}>
            <FieldRow>
              <Field label={t.full_name} required error={errors.full_name} name="full_name"><Input data-testid="app-full-name" value={f.full_name} onChange={(e) => update('full_name', e.target.value)} /></Field>
              <Field label={t.dob} required error={errors.dob} name="dob"><Input type="date" data-testid="app-dob" value={f.dob} onChange={(e) => update('dob', e.target.value)} /></Field>
            </FieldRow>
            <FieldRow>
              <Field label={t.phone} required error={errors.phone} name="phone"><Input type="tel" data-testid="app-phone" value={f.phone} onChange={(e) => update('phone', e.target.value)} /></Field>
              <Field label={t.email} required error={errors.email} name="email"><Input type="email" data-testid="app-email" value={f.email} onChange={(e) => update('email', e.target.value)} /></Field>
            </FieldRow>
            <Field label={t.address} required error={errors.address} name="address"><Input data-testid="app-address" value={f.address} onChange={(e) => update('address', e.target.value)} /></Field>
            <FieldRow cols={3}>
              <Field label={t.city} required error={errors.city} name="city"><Input data-testid="app-city" value={f.city} onChange={(e) => update('city', e.target.value)} /></Field>
              <Field label={t.state} required error={errors.state} name="state"><Input data-testid="app-state" value={f.state} onChange={(e) => update('state', e.target.value)} maxLength={2} /></Field>
              <Field label={t.zip} required error={errors.zip_code} name="zip_code"><Input data-testid="app-zip" value={f.zip_code} onChange={(e) => update('zip_code', e.target.value)} /></Field>
            </FieldRow>
          </FormSection>

          <FormSection title={t.section_position}>
            <Field label={t.position} required error={errors.position} name="position">
              <Select value={f.position} onValueChange={(v) => update('position', v)}>
                <SelectTrigger data-testid="app-position"><SelectValue placeholder={t.select_one} /></SelectTrigger>
                <SelectContent>
                  {POSITIONS_META.map((p) => (<SelectItem key={p.name} value={p.name}>{p.name}</SelectItem>))}
                  <SelectItem value="Other / Open to Any">Other / Open to Any</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <FieldRow>
              <Field label={t.years_experience} required error={errors.years_experience} name="years_experience">
                <Select value={f.years_experience} onValueChange={(v) => update('years_experience', v)}>
                  <SelectTrigger data-testid="app-experience"><SelectValue placeholder={t.select_one} /></SelectTrigger>
                  <SelectContent>{EXPERIENCE_OPTIONS.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}</SelectContent>
                </Select>
              </Field>
              <Field label={t.start_date} required error={errors.start_date} name="start_date"><Input type="date" data-testid="app-start-date" value={f.start_date} onChange={(e) => update('start_date', e.target.value)} /></Field>
            </FieldRow>
            <Field label={t.currently_employed} required error={errors.currently_employed} name="currently_employed">
              <YesNoRadio value={f.currently_employed} onChange={(v) => update('currently_employed', v)} testid="app-currently-employed" />
            </Field>
          </FormSection>

          <FormSection title={t.section_qualifications}>
            <Field label={t.drivers_license} required error={errors.has_drivers_license} name="has_drivers_license">
              <YesNoRadio value={f.has_drivers_license} onChange={(v) => update('has_drivers_license', v)} testid="app-drivers-license" />
            </Field>
            <Field label={t.transportation} required error={errors.has_transportation} name="has_transportation">
              <YesNoRadio value={f.has_transportation} onChange={(v) => update('has_transportation', v)} testid="app-transportation" />
            </Field>
            <Field label={t.heights} hint="Required for most roofing positions" required error={errors.comfortable_with_heights} name="comfortable_with_heights">
              <YesNoRadio value={f.comfortable_with_heights} onChange={(v) => update('comfortable_with_heights', v)} testid="app-heights" />
            </Field>
            <Field label={t.languages} required error={errors.languages} name="languages">
              <div className="flex flex-wrap gap-2" data-testid="app-languages">
                {LANG_OPTIONS.map((lng) => (
                  <button key={lng} type="button" onClick={() => toggleLang(lng)}
                          className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${f.languages.includes(lng) ? 'bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]' : 'bg-white border-border text-slate-700 hover:bg-slate-50'}`}
                          data-testid={`lang-opt-${lng.replace(/\s+/g, '-').toLowerCase()}`}>
                    {lng}
                  </button>
                ))}
              </div>
            </Field>
            <Field label={t.certifications} name="certifications"><Input data-testid="app-certifications" value={f.certifications} onChange={(e) => update('certifications', e.target.value)} /></Field>
            <Field label={t.work_auth} required error={errors.work_authorization} name="work_authorization">
              <YesNoRadio value={f.work_authorization} onChange={(v) => update('work_authorization', v)} testid="app-work-auth" />
            </Field>
          </FormSection>

          <FormSection title={t.section_about}>
            <Field label={t.why_join} name="why_join">
              <Textarea data-testid="app-why-join" rows={3} maxLength={500} value={f.why_join} onChange={(e) => update('why_join', e.target.value)} />
              <div className="text-xs text-slate-500 text-right mt-1">{(f.why_join || '').length}/500</div>
            </Field>
            <Field label={t.source} name="source">
              <Select value={f.source} onValueChange={(v) => update('source', v)}>
                <SelectTrigger data-testid="app-source"><SelectValue placeholder={t.select_one} /></SelectTrigger>
                <SelectContent>{SOURCE_OPTIONS.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}</SelectContent>
              </Select>
            </Field>
            <Field label={t.resume} hint="PDF/DOC/DOCX, max 10MB" name="resume">
              <Input type="file" accept=".pdf,.doc,.docx" data-testid="app-resume" onChange={(e) => update('resume', e.target.files?.[0] || null)} />
            </Field>
          </FormSection>

          <FormSection title={t.section_consent}>
            <label className={`flex items-start gap-3 cursor-pointer ${errors.consent ? 'text-red-600' : 'text-slate-700'}`} data-field="consent">
              <Checkbox checked={f.consent} onCheckedChange={(v) => update('consent', !!v)} className="mt-1" data-testid="app-consent" />
              <span className="text-sm leading-relaxed">{t.consent_text}</span>
            </label>
          </FormSection>

          <div className="mt-8">
            <Button onClick={submit} disabled={submitting} className="w-full h-12 bg-[hsl(var(--accent))] hover:bg-[#a01830] text-white font-semibold text-base" data-testid="app-submit">
              {submitting ? 'Submitting...' : t.submit_application}
            </Button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function FormSection({ title, children }) {
  return (
    <div className="mt-7">
      <h3 className="text-lg font-bold text-slate-900 border-b border-border pb-2 mb-4">{title}</h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function FieldRow({ children, cols = 2 }) {
  const gridClass = cols === 3 ? 'grid sm:grid-cols-3 gap-4' : 'grid sm:grid-cols-2 gap-4';
  return <div className={gridClass}>{children}</div>;
}

function Field({ label, required, error, hint, children, name }) {
  return (
    <div data-field={name}>
      <Label className="text-slate-800 mb-1.5 block">
        {label} {required && <span className="text-red-600">*</span>}
      </Label>
      {children}
      {hint && !error && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}

function YesNoRadio({ value, onChange, testid }) {
  return (
    <RadioGroup value={value} onValueChange={onChange} className="flex gap-4" data-testid={testid}>
      <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="yes" id={`${testid}-yes`} /><span>Yes</span></label>
      <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="no" id={`${testid}-no`} /><span>No</span></label>
    </RadioGroup>
  );
}
